/**
 * Ernaehrung (F-35): Lebensmittel, Rezepte, Tagesplaene, Vorlagen und
 * Einkaufsliste. Plan (`speiseplan`) und Gegessenes (`gegessen`) sind gleich
 * aufgebaut - dieselben Funktionen bedienen beide.
 */
import { db } from '@/db/schema';
import { eintragHinzufuegen, listeAnlegen } from '@/db/repositories/listen';
import { inDenPapierkorb, papierkorbTitel, rueckgaengigMachbar } from '@/db/repositories/papierkorb';
import { einkaufsliste } from '@/domain/ernaehrung/einkauf';
import {
  GETRAENKE, STANDARD_MAHLZEITEN, type ErnaehrungsEinstellungen, type Getraenk, type Lebensmittel, type Mahlzeit, type PlanTag, type PlanVorlage, type Posten, type Rezept,
} from '@/domain/ernaehrung/typen';
import type { ID, LocalDate } from '@/domain/types';
import { addDays, formatDate, formatDe, isoWeek, nowInstant } from '@/lib/datetime';
import { newId } from '@/lib/utils/id';

// --- Lebensmittel ---------------------------------------------------------------

export type LebensmittelEntwurf = Omit<Lebensmittel, 'id' | 'createdAt' | 'updatedAt'>;

export async function lebensmittelAnlegen(e: LebensmittelEntwurf): Promise<Lebensmittel> {
  const ts = nowInstant();
  const l: Lebensmittel = { ...e, name: e.name.trim() || 'Lebensmittel', id: newId(), createdAt: ts, updatedAt: ts };
  await db.lebensmittel.add(l);
  return l;
}

export async function lebensmittelAendern(id: ID, patch: Partial<LebensmittelEntwurf>): Promise<void> {
  await db.lebensmittel.update(id, { ...patch, updatedAt: nowInstant() });
}

/**
 * Benutzte werden nur archiviert, damit Plaene und Tagebuch ihre Werte
 * behalten (mit Rueckgaengig-Meldung); unbenutzte kommen fuer 30 Tage in den
 * Papierkorb (F-45).
 */
export async function lebensmittelLoeschen(id: ID): Promise<void> {
  const inRezept = (await db.rezepte.toArray()).some((r) => r.zutaten.some((z) => z.lebensmittelId === id));
  const inTag = async (t: typeof db.speiseplan) => (await t.toArray()).some((d) => d.mahlzeiten.some((m) => m.posten.some((p) => p.art === 'lebensmittel' && p.refId === id)));
  const b = { tabelle: 'lebensmittel', zeileIds: [id] };
  if (inRezept || (await inTag(db.speiseplan)) || (await inTag(db.gegessen))) {
    await rueckgaengigMachbar({ ...b, art: 'archiviert' }, () => lebensmittelAendern(id, { archiviert: true }), [db.lebensmittel]);
  } else {
    await inDenPapierkorb({ ...b, art: 'geloescht' }, () => db.transaction('rw', db.lebensmittel, db.attachments, async () => {
      await db.lebensmittel.delete(id);
      // Behaltenes Foto der Naehrwerttabelle (B) gehoert zum Lebensmittel.
      await db.attachments.where('resourceId').equals(id).delete();
    }));
  }
}

/** Behaltenes Foto der Naehrwerttabelle loeschen - 30 Tage im Papierkorb (F-45). */
export async function lebensmittelFotoLoeschen(anhangId: ID): Promise<void> {
  const resourceId = (await db.attachments.get(anhangId))?.resourceId;
  const l = resourceId ? await db.lebensmittel.get(resourceId) : undefined;
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'attachments', zeileIds: [anhangId], titel: papierkorbTitel(l && `Foto: ${l.name}`) },
    () => db.attachments.delete(anhangId));
}

async function benutzt(p: Posten): Promise<void> {
  const ts = nowInstant();
  if (p.art === 'lebensmittel') await db.lebensmittel.where('id').equals(p.refId).modify((l) => { l.zuletzt = ts; l.anzahl = (l.anzahl ?? 0) + 1; });
  if (p.art === 'rezept') await db.rezepte.where('id').equals(p.refId).modify((r) => { r.zuletzt = ts; r.anzahl = (r.anzahl ?? 0) + 1; });
}

// --- Rezepte --------------------------------------------------------------------

export async function rezeptAnlegen(name: string): Promise<Rezept> {
  const ts = nowInstant();
  const r: Rezept = { id: newId(), name: name.trim() || 'Neues Rezept', portionen: 1, zutaten: [], createdAt: ts, updatedAt: ts };
  await db.rezepte.add(r);
  return r;
}

export async function rezeptAendern(id: ID, patch: Partial<Omit<Rezept, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> {
  await db.rezepte.update(id, { ...patch, updatedAt: nowInstant() });
}

export const rezeptLoeschen = (id: ID): Promise<void> =>
  inDenPapierkorb({ art: 'geloescht', tabelle: 'rezepte', zeileIds: [id] }, () => db.rezepte.delete(id));

// --- Tage (Plan und Gegessenes) -------------------------------------------------

export type TagTabelle = 'speiseplan' | 'gegessen';

export async function mahlzeitNamen(): Promise<string[]> {
  const e = await db.ernaehrungsEinstellungen.get('standard');
  return e?.mahlzeiten?.length ? e.mahlzeiten : STANDARD_MAHLZEITEN;
}

export const leereMahlzeiten = (namen: string[]): Mahlzeit[] => namen.map((name) => ({ id: newId(), name, posten: [] }));

/** Einen Tag aendern - legt ihn mit den Standard-Mahlzeiten an, wenn es ihn noch nicht gibt. */
export async function tagAendern(tabelle: TagTabelle, datum: LocalDate, f: (mahlzeiten: Mahlzeit[]) => void): Promise<void> {
  const t = db[tabelle];
  await db.transaction('rw', t, db.ernaehrungsEinstellungen, async () => {
    const alt = await t.get(datum);
    const mahlzeiten = structuredClone(alt?.mahlzeiten ?? leereMahlzeiten(await mahlzeitNamen()));
    f(mahlzeiten);
    await t.put({ ...alt, id: datum, datum, mahlzeiten, updatedAt: nowInstant() });
  });
}

/** Tag von Hand als vollstaendig erfasst markieren (C) - `undefined` = automatisch nach Mahlzeiten. */
export async function tagVollstaendig(datum: LocalDate, wert: boolean | undefined): Promise<void> {
  await tagAendern('gegessen', datum, () => {});
  await db.gegessen.where('id').equals(datum).modify((t) => { if (wert === undefined) delete t.vollstaendig; else t.vollstaendig = wert; });
}

export async function postenHinzufuegen(tabelle: TagTabelle, datum: LocalDate, mahlzeit: string, p: Posten): Promise<void> {
  await tagAendern(tabelle, datum, (mz) => {
    let m = mz.find((x) => x.name === mahlzeit);
    if (!m) { m = { id: newId(), name: mahlzeit, posten: [] }; mz.push(m); }
    m.posten.push(p);
  });
  await benutzt(p);
}

export const postenEntfernen = (tabelle: TagTabelle, datum: LocalDate, postenId: ID) =>
  tagAendern(tabelle, datum, (mz) => { for (const m of mz) m.posten = m.posten.filter((p) => p.id !== postenId); });

export const postenMenge = (tabelle: TagTabelle, datum: LocalDate, postenId: ID, menge: number, anzeige?: string) =>
  tagAendern(tabelle, datum, (mz) => {
    for (const m of mz) for (const p of m.posten) if (p.id === postenId && p.art !== 'schnell') { p.menge = menge; if (p.art === 'lebensmittel') { if (anzeige) p.anzeige = anzeige; else delete p.anzeige; } }
  });

/** Posten neu kopieren - mit neuen IDs, damit Plan und Kopie unabhaengig bleiben. */
const kopie = (mz: Mahlzeit[]): Mahlzeit[] => mz.map((m) => ({ id: newId(), name: m.name, posten: m.posten.map((p) => ({ ...structuredClone(p), id: newId() })) }));

/** Mahlzeiten an einen Tag anhaengen (Vorlage, Kopie): gleiche Mahlzeiten werden zusammengefuehrt. */
function anhaengen(ziel: Mahlzeit[], neu: Mahlzeit[]): void {
  for (const m of neu) {
    const da = ziel.find((x) => x.name === m.name);
    if (da) da.posten.push(...m.posten); else ziel.push(m);
  }
}

export async function tagKopieren(von: TagTabelle, vonDatum: LocalDate, nach: TagTabelle, nachDatum: LocalDate): Promise<void> {
  const quelle = await db[von].get(vonDatum);
  if (!quelle) return;
  await tagAendern(nach, nachDatum, (mz) => anhaengen(mz, kopie(quelle.mahlzeiten)));
}

export const tagLeeren = (tabelle: TagTabelle, datum: LocalDate): Promise<void> =>
  inDenPapierkorb({ art: 'geloescht', tabelle, zeileIds: [datum], titel: formatDate(datum) }, () => db[tabelle].delete(datum));

// --- Vorlagen -------------------------------------------------------------------

export async function alsVorlage(datum: LocalDate, name: string): Promise<PlanVorlage | null> {
  const tag = await db.speiseplan.get(datum);
  if (!tag) return null;
  const ts = nowInstant();
  const v: PlanVorlage = { id: newId(), name: name.trim() || `Tag vom ${formatDe(datum, 'dd.MM.')}`, mahlzeiten: kopie(tag.mahlzeiten), createdAt: ts, updatedAt: ts };
  await db.planVorlagen.add(v);
  return v;
}

export async function vorlageAnwenden(vorlageId: ID, datum: LocalDate): Promise<void> {
  const v = await db.planVorlagen.get(vorlageId);
  if (v) await tagAendern('speiseplan', datum, (mz) => anhaengen(mz, kopie(v.mahlzeiten)));
}

export const vorlageLoeschen = (id: ID): Promise<void> =>
  inDenPapierkorb({ art: 'geloescht', tabelle: 'planVorlagen', zeileIds: [id] }, () => db.planVorlagen.delete(id));

// --- Einkaufsliste --------------------------------------------------------------

/** Erzeugt aus dem Plan der Woche ab `montag` eine Einkaufsliste in den Listen (F-33). */
export async function einkaufslisteErzeugen(montag: LocalDate): Promise<{ listeId: ID; anzahl: number } | null> {
  const [tage, lebensmittel, rezepte] = await Promise.all([
    db.speiseplan.where('datum').between(montag, addDays(montag, 6), true, true).toArray(),
    db.lebensmittel.toArray(), db.rezepte.toArray(),
  ]);
  const posten = einkaufsliste(tage as PlanTag[], { lebensmittel: new Map(lebensmittel.map((l) => [l.id, l])), rezepte: new Map(rezepte.map((r) => [r.id, r])) });
  if (!posten.length) return null;
  const liste = await listeAnlegen('einkauf', `Einkauf KW ${isoWeek(montag)} (Speiseplan)`);
  for (const p of posten) await eintragHinzufuegen(liste.id, p.name, { menge: p.text, ...(p.abteilung ? { gruppe: p.abteilung } : {}) });
  return { listeId: liste.id, anzahl: posten.length };
}

// --- Erfassen (B) -----------------------------------------------------------------

/**
 * Geplantes als gegessen uebernehmen - eine Mahlzeit oder den ganzen Tag.
 * Schon Uebernommenes (gleicher Plan-Posten) wird nicht doppelt eingetragen.
 */
export async function planUebernehmen(datum: LocalDate, mahlzeitName?: string): Promise<number> {
  const plan = await db.speiseplan.get(datum);
  if (!plan) return 0;
  let n = 0;
  await tagAendern('gegessen', datum, (mz) => {
    const schon = new Set(mz.flatMap((m) => m.posten.map((p) => p.vonPlan)).filter(Boolean));
    for (const pm of plan.mahlzeiten) {
      if (mahlzeitName && pm.name !== mahlzeitName) continue;
      const neu = pm.posten.filter((p) => !schon.has(p.id)).map((p) => ({ ...structuredClone(p), id: newId(), vonPlan: p.id }));
      if (!neu.length) continue;
      let ziel = mz.find((m) => m.name === pm.name);
      if (!ziel) { ziel = { id: newId(), name: pm.name, posten: [] }; mz.push(ziel); }
      ziel.posten.push(...neu);
      n += neu.length;
    }
  });
  return n;
}

export async function getraenkHinzufuegen(datum: LocalDate, art: Getraenk['art'], ml: number, zeit: string, lebensmittelId?: ID): Promise<void> {
  if (!(ml > 0) || ml > 5000) return;
  await db.getraenke.add({ id: newId(), datum, zeit, art, ml: Math.round(ml), ...(lebensmittelId ? { lebensmittelId } : {}), createdAt: nowInstant() });
}

export async function getraenkLoeschen(id: ID): Promise<void> {
  const g = await db.getraenke.get(id);
  const titel = g ? `${GETRAENKE[g.art] ?? g.art}, ${g.ml} ml um ${g.zeit}` : undefined;
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'getraenke', zeileIds: [id], titel }, () => db.getraenke.delete(id));
}

export async function einstellungenAendern(patch: Partial<Omit<ErnaehrungsEinstellungen, 'id' | 'updatedAt'>>): Promise<void> {
  await db.transaction('rw', db.ernaehrungsEinstellungen, async () => {
    const alt = (await db.ernaehrungsEinstellungen.get('standard')) ?? { id: 'standard' as const, ziele: {}, updatedAt: '' };
    await db.ernaehrungsEinstellungen.put({ ...alt, ...patch, updatedAt: nowInstant() });
  });
}
